<template>
<div>
	<register-view v-if="registerToken" :registerToken="registerToken"/>
	<password-forgot-view v-if="(pathname === '/passwordreset')" :token="resetToken"/>
	<login-view v-if="!loggedIn"/>
	<!-- Adyen onboarding: waiting for customer to start (pending/submitted) -->
	<div v-if="loggedIn && adyenNotification && (adyenNotification.status === 'pending' || adyenNotification.status === 'submitted')" class="flex min-h-screen items-center justify-center bg-gray-50">
		<div class="max-w-md text-center p-8 bg-white rounded-lg shadow-md">
			<div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
				<svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
				</svg>
			</div>
			<h2 class="text-lg font-semibold text-gray-900 mb-2">Verifizierung erforderlich</h2>
			<p class="text-sm text-gray-600 mb-4">
				Ihr Zahlungsdienstleister hat das Onboarding fuer Ihr Unternehmen gestartet.
				Bitte klicken Sie auf die Schaltflaeche unten, um die Verifizierung zu starten.
			</p>
			<button
				@click="regenerateOnboardingLink"
				:disabled="regeneratingOnboarding"
				class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
			>
				<span v-if="regeneratingOnboarding">Wird geladen...</span>
				<span v-else>Verifizierung starten</span>
			</button>
		</div>
	</div>

	<!-- Adyen onboarding: rejected -->
	<div v-if="loggedIn && adyenNotification && adyenNotification.status === 'rejected'" class="flex min-h-screen items-center justify-center bg-gray-50">
		<div class="max-w-md text-center p-8 bg-white rounded-lg shadow-md">
			<div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
				<svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
				</svg>
			</div>
			<h2 class="text-lg font-semibold text-gray-900 mb-2">Onboarding abgelehnt</h2>
			<p class="text-sm text-gray-600">
				Die Geschaeftsbedingungen wurden abgelehnt. Bitte kontaktieren Sie Ihren Ansprechpartner bei Pay-Jet.
			</p>
		</div>
	</div>

	<!-- Normal portal content — only when no onboarding is blocking -->
	<div v-if="loggedIn && (!adyenNotification || adyenNotification.status === 'verified' || adyenNotification.status === 'in_progress' || !['pending', 'submitted', 'rejected'].includes(adyenNotification.status))" class="h-full flex">

		<desktop-nav
			:navigation="navigation"
			:user="user"
		/>

		<div class="flex-1 min-w-0 flex flex-col overflow-hidden">
			<!-- Mobile top navigation -->
			<mobile-nav
				:navigation="navigation"
				:user="user"
			/>

			<!-- Adyen onboarding resume banner -->
			<div v-if="adyenNotification && adyenNotification.status === 'in_progress'" class="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-4">
				<div class="flex items-center gap-3">
					<svg class="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<p class="text-sm text-amber-800">
						Ihre Adyen-Verifizierung ist noch nicht abgeschlossen.
					</p>
				</div>
				<button
					type="button"
					:disabled="regeneratingOnboarding"
					@click="regenerateOnboardingLink"
					class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<span v-if="regeneratingOnboarding">Wird geladen...</span>
					<span v-else>Verifizierung fortsetzen</span>
				</button>
			</div>

			<main class="flex-1 flex"  style="max-height: -webkit-fill-available">
				<!-- Primary column -->
				<section aria-labelledby="primary-heading"
						 class="flex-1 h-full flex flex-col overflow-y-auto overflow-x-hidden lg:order-first scrollbar-hide"
				>
					<router-view/>
					<!-- Your content -->
				</section>

			</main>
		</div>
	</div>
</div>
</template>

<script>
import {
	BookmarkAltIcon,
	BriefcaseIcon,
	ChartBarIcon,
	ClipboardIcon,
	CollectionIcon,
	FireIcon,
	HomeIcon,
	InboxIcon,
	MenuIcon,
	UserIcon,
	XIcon
} from '@heroicons/vue/outline'
import {defineComponent, markRaw, onMounted, onUnmounted, ref, watch} from "vue";
import MobileNav from "@/component/Navigation/MobileNav";
import DesktopNav from "@/component/Navigation/DesktopNav";
import {useFetchBranchlocationsDispatch, useFetchCustomersDispatch} from "@/store/composables";
import LoginView from "@/views/LoginView";
import RegisterView from "@/views/RegisterView";
import {useIdle} from "@vueuse/core";
import PasswordForgotView from "@/views/PasswordForgotView.vue";
import { UsersIcon } from "@heroicons/vue/solid";
import LiberisIcon from '@/component/Icon/LiberisIcon.vue';
import { getAdyenOnboardingStatus, respondToOnboarding } from '@/api/adyenNotification';

export default defineComponent({
	name: "App",
	components: {
		PasswordForgotView,
		LoginView,
		RegisterView,
		DesktopNav,
		MobileNav,
		BookmarkAltIcon,
		FireIcon,
		HomeIcon,
		InboxIcon,
		MenuIcon,
		UserIcon,
		XIcon,
		useIdle,
    LiberisIcon
	},
	watch: {
		$route: {
			immediate: true,
			handler(to, from) {
				document.title = to.meta.title || 'Pay-Jet Kundenportal';
			}
		}
	},
	setup() {

		onMounted(useFetchCustomersDispatch)
		onMounted(useFetchBranchlocationsDispatch)

		const {idle} = useIdle(1000 * 60 * 30);

		const loggedIn = ref(!!window.localStorage.getItem("auth_token"));
		const user = ref({
			loggedIn: loggedIn,
			name: "",
			email: "",
			imageUrl: "",
		});
		setInterval(() => {
			const token = window.localStorage.getItem("auth_token");
			loggedIn.value = !!token;
			if (idle.value === true) {
				window.localStorage.removeItem("auth_token");
			}
		}, 100);

		const urlParams = new URLSearchParams(window.location.search);
		const registerToken = urlParams.get('register_token');
		const resetToken = urlParams.get('token');
		const pathname = window.location.pathname;

		// Adyen onboarding notification
		const adyenNotification = ref(null);
		const regeneratingOnboarding = ref(false);
		let adyenPollInterval = null;
		const regenerateOnboardingLink = async () => {
			if (!adyenNotification.value || regeneratingOnboarding.value) return;
			regeneratingOnboarding.value = true;
			try {
				const result = await respondToOnboarding(adyenNotification.value.id, "accept");
				if (result.success && result.onboarding_url) {
					window.open(result.onboarding_url, '_blank');
				}
			} finally {
				regeneratingOnboarding.value = false;
			}
		};

		const checkAdyenOnboarding = async () => {
			if (!window.localStorage.getItem("auth_token")) return;
			try {
				const status = await getAdyenOnboardingStatus();
				if (status) {
					adyenNotification.value = status;
				}
			} catch {
				// ignore
			}
		};

		// Poll for status changes during blocking states
		const startPolling = () => {
			if (adyenPollInterval) return;
			adyenPollInterval = setInterval(checkAdyenOnboarding, 30000); // every 30s
		};

		const stopPolling = () => {
			if (adyenPollInterval) {
				clearInterval(adyenPollInterval);
				adyenPollInterval = null;
			}
		};

		watch(adyenNotification, (val) => {
			const status = val?.status;
			if (status && ['pending', 'submitted', 'in_progress'].includes(status)) {
				startPolling();
			} else {
				stopPolling();
			}

			// Add/remove Adyen Transactions nav item based on verified status
			const adyenNavIndex = navigation.value.findIndex(n => n.href === '/adyen-transactions');
			if (status === 'verified' && adyenNavIndex === -1) {
				navigation.value.splice(1, 0, {
					name: 'AdyenTransactions',
					href: '/adyen-transactions',
					meta: { title: 'Adyen Transaktionen' },
					icon: markRaw(ChartBarIcon),
				});
			} else if (status !== 'verified' && adyenNavIndex !== -1) {
				navigation.value.splice(adyenNavIndex, 1);
			}
		});

		onMounted(checkAdyenOnboarding);
		onUnmounted(stopPolling);

		const navigation = ref([
			{
				name: 'Transactions',
				href: '/transactions',
				meta: {title: "Transaktionen"},
				icon: ChartBarIcon
			},
			{
				name: 'Invoices',
				href: '/invoices',
				meta: {title: "Rechnungen"},
				icon: ClipboardIcon
			},
			// {name: 'Statements', href: '/statements', icon: CollectionIcon},
			{
				name: 'Organization',
				href: '/organization',
				meta: {title: "Organisation"},
				icon: BriefcaseIcon
			},
			{
				name: 'Cashregisters',
				href: '/cashregisters',
				meta: {title: "Kassenschnitte"},
				icon: CollectionIcon
			},
			{
				name: 'Partner',
				href: '/partner',
				meta: {title: "Kassenpartner"},
				icon: UsersIcon
			},
			{
				name: 'Liberis',
				href: '/liberis',
				meta: {title: "Liberis"},
				icon: LiberisIcon,
        classes: ""
			}
		]);


		return {
			user,
			loggedIn,
			navigation,
			registerToken,
			resetToken,
			idle,
			pathname,
			adyenNotification,
			regeneratingOnboarding,
			regenerateOnboardingLink,
		}
	}
});
</script>
